import React, { useEffect, useMemo, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction, i18n } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3120CustomerFormExtendedHandlers } from "../../../U3120CustomerFormTypes";
import { u3120CustomerContactHandler } from "./U3120CustomerContactHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { emptyCustomerContact, type U3120CustomerContact, U3120ContactPhoneType } from "../../../../U3120Service";
import { NS, U3120ROLES } from "../../../../U3120";
import U3120CustomerContactDeleteDlg from "./U3120CustomerContactDeleteDlg";
import U3120CustomerContactEditDlg from "./U3120CustomerContactEditDlg";
import { u3120ValidateCustomerContact } from "../../../../tools/U3120CustomerContactValidation";

// Typage des props du composant
interface U3120CustomerContactsComponentProps {
  u3120CustomerFormHandlers: U3120CustomerFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des contacts d'un client
function getColumnsDef(t: TFunction, i18n: i18n): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "contactName",
      label: t("lblContactName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[30%]",
      type: "string",
      align: "left",
    },
    {
      name: "contactType",
      label: t("lblContactType", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_column: any, value: any, _row: any) => {
        if (!value) return "";
        const lang = i18n?.language || "fr";
        if (lang === "fr" && value.contactTypeNameFr) {
          return value.contactTypeNameFr;
        }
        if (lang === "en" && value.contactTypeNameEn) {
          return value.contactTypeNameEn;
        }
        return value.contactTypeCode || "";
      },
    },
    {
      name: "email",
      label: t("lblEmail", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[20%]",
      type: "string",
      align: "left",
    },
    {
      name: "phone1",
      label: t("lblPhone1", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "string",
      align: "left",
    },
    {
      name: "phone1Type",
      label: t("lblPhoneType", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "string",
      align: "left",
      display: (_column: any, value: any) => {
        if (!value) return "";
        if (value === U3120ContactPhoneType.OFFICE) return t("lblPhoneTypeOffice", { ns: NS });
        if (value === U3120ContactPhoneType.HOME) return t("lblPhoneTypeHome", { ns: NS });
        if (value === U3120ContactPhoneType.CELLPHONE) return t("lblPhoneTypeCellphone", { ns: NS });
        return String(value);
      },
    },
    {
      name: "phone1Extension",
      label: t("lblPhone1Extension", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[8%]",
      type: "string",
      align: "left",
      display: (_column: any, value: any, row: any) => {
        if (row?.phone1Type !== U3120ContactPhoneType.OFFICE) return "";
        return value ?? "";
      },
    },
    {
      name: "notes",
      label: t("lblNotes", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
  ];
}

// Composant principal pour la gestion des contacts d'un client
const U3120CustomerContactBody: React.FC<U3120CustomerContactsComponentProps> = ({ u3120CustomerFormHandlers }) => {
  // Translation
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U3120CustomerContact>();

  const validateCustomerContact = (customerContact: U3120CustomerContact) =>
    u3120ValidateCustomerContact(customerContact, gridStateHandlers.getData(), t);

  const u3120CustomerContactBL: U2000FormSBusinessHandlers<U3120CustomerContact> = {
    newRecord: () => ({ ...emptyCustomerContact }),

    // validation du formulaire
    validateForm: validateCustomerContact,
  };

  const u3120CustomerContactformStateHandlers = useU2000FormStateHandlers<U3120CustomerContact>({
    formState,
    formStateDispatch,
    businessHandlers: u3120CustomerContactBL,
  });

  const u3120CustomerContactHandlers = u3120CustomerContactHandler({ gridStateHandlers, t, formStateHandlers: u3120CustomerContactformStateHandlers, parentFormHandlers: u3120CustomerFormHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour CustomerContacts
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t, i18n);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const customerContacts = u3120CustomerFormHandlers.getData?.()?.customerContacts;
    gridStateHandlers.setData(Array.isArray(customerContacts) ? customerContacts : []);
    gridStateHandlers.setTotalRows(Array.isArray(customerContacts) ? customerContacts.length : 0);

    // Gérer les erreurs spécifiques au champ CustomerContacts
    const errorsField = u3120CustomerFormHandlers.getOtherErrors()?.customerContacts;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u3120CustomerFormHandlers, t, i18n]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3120CustomerContactHandlers.dataChangedEvent === 0) return;

    const customerContacts = gridStateHandlers.getData();
    u3120CustomerFormHandlers.setData({
      ...u3120CustomerFormHandlers.getData?.(),
      customerContacts: customerContacts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u3120CustomerContactHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3120CustomerContactHandlers.onAdd,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3120CustomerContactHandlers.onModify,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u3120CustomerContactHandlers.onDelete,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3120CustomerContactHandlers],
  );

  // Render
  return (
    <>
      <U3120CustomerContactEditDlg u3120CustomerContactHandler={u3120CustomerContactHandlers} t={t} NS={NS} />
      <U3120CustomerContactDeleteDlg u3120CustomerContactHandler={u3120CustomerContactHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={u3120CustomerContactHandlers.onRowClick as unknown as (row: any) => void}
        allowRowSelection={u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblCustomerContact", {
          count: u3120CustomerContactHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3120CustomerContactBody;
